import React, { useState, useEffect } from 'react';
import { Award, LinkItem, Sponsor } from '../types';
import { 
  appLinks, 
  sponsors, 
  friends, 
  mosaicImages, 
  taglines, 
  awards, 
  socialLinks 
} from '../config';

const Home: React.FC = () => {
  const [selectedAward, setSelectedAward] = useState<Award | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [currentTaglineIndex, setCurrentTaglineIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Tagline rotation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentTaglineIndex((prevIndex) => (prevIndex + 1) % taglines.length);
        setIsTransitioning(false);
      }, 300); // Half of the transition duration
    }, 15000);

    return () => clearInterval(interval);
  }, [taglines.length]);

  // Helper function to detect if media link is a YouTube video
  const isYouTubeVideo = (url: string): boolean => {
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  // Helper function to get YouTube embed URL
  const getYouTubeEmbedUrl = (url: string): string => {
    if (url.includes('embed')) return url;
    const videoId = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
    return videoId ? `https://www.youtube.com/embed/${videoId[1]}` : url;
  };

  // Handle award click
  const handleAwardClick = (award: Award) => {
    setSelectedAward(award);
    setShowPopup(true);
  };

  // Handle popup close
  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedAward(null);
  };

  return (
    <div className="min-h-screen bg-transparent p-0 relative bg-gray-900">
      <div className="max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto p-8 md:p-12 lg:p-16 py-16 flex flex-col justify-start relative">
        {/* Profile Section */}
        <div className="text-center mb-2 animate-fadeInUp">
          <div className="mb-6 flex justify-center">
            <img 
              src="/l5_simple.svg" 
              alt="Level 5 Robotics Logo"
              className="w-32 h-32 transition-all duration-300 object-contain"
            />
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-2 tracking-tight bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">
            Level 5 Robotics
          </h1>
          <p 
            className={`text-lg md:text-xl text-gray-300 mb-4 font-medium transition-all duration-600 ease-in-out ${
              isTransitioning 
                ? 'opacity-0 transform translate-y-2' 
                : 'opacity-100 transform translate-y-0'
            }`}
          >
            {taglines[currentTaglineIndex]}
          </p>
          <div className="flex justify-center gap-4 mt-2">
            <button 
              className="bg-gray-800/50 border-2 border-yellow-400/60 text-3xl cursor-pointer transition-all duration-300 p-3 rounded-xl flex items-center justify-center hover:scale-110 hover:bg-gray-700/50 hover:border-yellow-400/80 hover:shadow-lg hover:shadow-yellow-400/20 focus:outline-none focus:ring-2 focus:ring-yellow-400/80 focus:ring-offset-2"
              onClick={() => handleAwardClick(awards[0])}
              aria-label="2024 RCL National Champion"
              title="2024 RCL National Champion"
            >
              🥇
            </button>
            <button 
              className="bg-gray-800/50 border-2 border-gray-400/60 text-3xl cursor-pointer transition-all duration-300 p-3 rounded-xl flex items-center justify-center hover:scale-110 hover:bg-gray-700/50 hover:border-gray-400/80 hover:shadow-lg hover:shadow-gray-400/20 focus:outline-none focus:ring-2 focus:ring-gray-400/80 focus:ring-offset-2"
              onClick={() => handleAwardClick(awards[1])}
              aria-label="2025 RCL National Finalist"
              title="2025 RCL National Finalist"
            >
              🥈
            </button>
          </div> 
        </div>

        {/* App Links Section */}
        <div className="my-12 md:my-16">
          <h3 className="text-lg md:text-xl font-semibold text-gray-300 mb-4 text-center tracking-wide relative">
            Combat Robotics
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-10 h-0.5 bg-gradient-to-r from-transparent via-purple-400/60 to-transparent"></div>
          </h3>
          <div className="flex flex-col gap-3">
            {appLinks.map((link: LinkItem, index: number) => {
              const isDisabled = link.disabled;
              return (
                <a 
                  key={index}
                  href={isDisabled ? "#" : link.url}
                  className={`flex items-center p-4 md:p-5 bg-gray-800/90 border border-purple-500/20 rounded-xl text-inherit transition-all duration-300 cursor-pointer relative overflow-hidden w-full text-left font-inherit text-inherit backdrop-blur-sm hover:bg-gray-700/90 hover:border-purple-500/80 hover:-translate-y-1 hover:shadow-lg hover:shadow-purple-500/30 focus:outline-none focus:ring-2 focus:ring-purple-500/80 focus:ring-offset-2 ${isDisabled ? 'pointer-events-none opacity-60 cursor-not-allowed grayscale' : ''} animate-fadeInUp`}
                  style={{ animationDelay: `${(index + 1) * 0.1}s` }}
                  target={isDisabled ? undefined : "_blank"}
                  rel={isDisabled ? undefined : "noopener noreferrer"}
                  tabIndex={isDisabled ? -1 : 0}
                  aria-disabled={isDisabled ? "true" : undefined}
                >
                  <div className="text-2xl mr-4 min-w-10 flex items-center justify-center">{link.icon}</div>
                  <div className="flex-1 text-left">
                    <h3 className="text-base md:text-lg font-semibold text-purple-400/90 mb-1 leading-tight">
                      {link.title}
                      {link.isNew && <span className="bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-semibold px-2 py-1 rounded-xl ml-2 uppercase tracking-wide shadow-lg shadow-red-500/30 animate-pulse">New</span>}
                      {link.comingSoon && <span className="bg-gradient-to-r from-indigo-500 to-purple-400 text-white text-xs font-semibold px-2 py-1 rounded-xl ml-2 uppercase tracking-wide shadow-lg shadow-indigo-500/15 opacity-85">Coming Soon</span>}
                    </h3>
                    <p className="text-sm md:text-base text-gray-300 leading-tight">{link.description}</p>
                  </div>
                  <div className="text-xl text-gray-500 ml-4 transition-all duration-300 group-hover:text-purple-500">→</div>
                </a>
              );
            })}
          </div>
        </div>

        {/* Rival Robotics Section */}
        <div className="my-12 md:my-16">
          <h3 className="text-lg md:text-xl font-semibold text-gray-300 mb-4 text-center tracking-wide relative">
            Rival Robotics
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-10 h-0.5 bg-gradient-to-r from-transparent via-red-500/60 to-transparent"></div>
          </h3>
          <div className="flex flex-col gap-3">
            <a 
              href="/rival"
              className="flex items-center p-4 md:p-5 bg-gray-800/90 border border-red-500/20 rounded-xl text-inherit transition-all duration-300 cursor-pointer relative overflow-hidden w-full text-left font-inherit text-inherit backdrop-blur-sm hover:bg-gray-700/90 hover:border-red-500/80 hover:-translate-y-1 hover:shadow-lg hover:shadow-red-500/30 focus:outline-none focus:ring-2 focus:ring-red-500/80 focus:ring-offset-2 animate-fadeInUp"
              style={{ animationDelay: '0.5s' }}
            >
              <div className="text-2xl mr-4 min-w-10 flex items-center justify-center">🤖</div>
              <div className="flex-1 text-left">
                <h3 className="text-base md:text-lg font-semibold text-red-400/90 mb-1 leading-tight">
                  Our Robots
                </h3>
                <p className="text-sm md:text-base text-gray-300 leading-tight">Explore our competition robots and their achievements.</p>
              </div>
              <div className="text-xl text-gray-500 ml-4 transition-all duration-300 group-hover:text-red-500">→</div>
            </a>
            <a 
              href="https://rivalrobotics.co/"
              className="flex items-center p-4 md:p-5 bg-gray-800/90 border border-red-500/20 rounded-xl text-inherit transition-all duration-300 cursor-pointer relative overflow-hidden w-full text-left font-inherit text-inherit backdrop-blur-sm hover:bg-gray-700/90 hover:border-red-500/80 hover:-translate-y-1 hover:shadow-lg hover:shadow-red-500/30 focus:outline-none focus:ring-2 focus:ring-red-500/80 focus:ring-offset-2 animate-fadeInUp"
              style={{ animationDelay: '0.6s' }}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="text-2xl mr-4 min-w-10 flex items-center justify-center">🏆</div>
              <div className="flex-1 text-left">
                <h3 className="text-base md:text-lg font-semibold text-red-400/90 mb-1 leading-tight">
                  Rival Robotics Competition
                </h3>
                <p className="text-sm md:text-base text-gray-300 leading-tight">A game-based robotics competition with no hardware or age limits.</p>
              </div>
              <div className="text-xl text-gray-500 ml-4 transition-all duration-300 group-hover:text-red-500">→</div>
            </a>
          </div>
        </div>

        {/* Sponsors Section */}
        <div className="my-12 md:my-16">
          <h3 className="text-lg md:text-xl font-semibold text-gray-300 mb-4 text-center tracking-wide relative">
            Sponsors
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-10 h-0.5 bg-gradient-to-r from-transparent via-yellow-500/60 to-transparent"></div>
          </h3>
          <div className="flex flex-col gap-3">
            {sponsors.map((sponsor: Sponsor, index: number) => (
              <a 
                key={index}
                href={sponsor.url}
                className="flex items-center p-4 md:p-5 bg-gray-800/90 border border-yellow-500/20 rounded-xl text-inherit transition-all duration-300 cursor-pointer relative overflow-hidden w-full text-left font-inherit text-inherit backdrop-blur-sm hover:bg-gray-700/90 hover:border-yellow-500/80 hover:-translate-y-1 hover:shadow-lg hover:shadow-yellow-500/30 focus:outline-none focus:ring-2 focus:ring-yellow-500/80 focus:ring-offset-2 animate-fadeInUp"
                style={{ animationDelay: `${(index + 1) * 0.1}s` }}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="mr-4 w-20 h-10 flex items-center justify-center rounded-lg overflow-hidden bg-white/10 p-1">
                  <img src={sponsor.logo} alt={sponsor.name} className="w-full h-full object-contain rounded" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-base md:text-lg font-semibold text-yellow-400/90 mb-1 leading-tight">{sponsor.name}</h3>
                  <p className="text-sm md:text-base text-gray-300 leading-tight">{sponsor.description}</p>
                </div>
                <div className="text-xl text-gray-500 ml-4 transition-all duration-300 group-hover:text-yellow-500">→</div>
              </a>
            ))}
          </div>
        </div>

        {/* Friends of L5 Section */}
        <div className="my-12 md:my-16">
          <h3 className="text-lg md:text-xl font-semibold text-gray-300 mb-4 text-center tracking-wide relative">
            Friends of L5
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-10 h-0.5 bg-gradient-to-r from-transparent via-pink-500/60 to-transparent"></div>
          </h3>
          <div className="flex flex-col gap-3">
            {friends.map((friend: Sponsor, index: number) => (
              <a 
                key={index}
                href={friend.url}
                className="flex items-center p-4 md:p-5 bg-gray-800/90 border border-pink-500/20 rounded-xl text-inherit transition-all duration-300 cursor-pointer relative overflow-hidden w-full text-left font-inherit text-inherit backdrop-blur-sm hover:bg-gray-700/90 hover:border-pink-500/80 hover:-translate-y-1 hover:shadow-lg hover:shadow-pink-500/30 focus:outline-none focus:ring-2 focus:ring-pink-500/80 focus:ring-offset-2 animate-fadeInUp"
                style={{ animationDelay: `${(index + 1) * 0.1}s` }}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="mr-4 w-20 h-10 flex items-center justify-center rounded-lg overflow-hidden bg-white/10 p-1">
                  <img src={friend.logo} alt={friend.name} className="w-full h-full object-contain rounded" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-base md:text-lg font-semibold text-pink-400/90 mb-1 leading-tight">{friend.name}</h3>
                  <p className="text-sm md:text-base text-gray-300 leading-tight">{friend.description}</p>
                </div>
                <div className="text-xl text-gray-500 ml-4 transition-all duration-300 group-hover:text-pink-500">→</div>
              </a>
            ))}
          </div>
        </div>

        {/* Social Icons Section */}
        <div className="text-center my-12 md:my-16 py-8 md:py-12 border-t border-gray-600/30">
          <div className="flex justify-center items-center gap-6 md:gap-8 lg:gap-10 flex-wrap">
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.url}
                className={`flex items-center justify-center w-12 h-12 md:w-14 md:h-14 lg:w-15 lg:h-15 rounded-full bg-gray-800/80 border border-gray-600/60 text-gray-300 transition-all duration-300 text-inherit backdrop-blur-sm relative overflow-hidden hover:scale-110 hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-cyan-400/80 focus:ring-offset-3 ${social.icon === 'instagram' ? 'hover:bg-gradient-to-r hover:from-pink-500/20 hover:to-yellow-500/20 hover:border-pink-500 hover:text-pink-500 hover:shadow-lg hover:shadow-pink-500/30' : social.icon === 'youtube' ? 'hover:bg-red-500/20 hover:border-red-500 hover:text-red-500 hover:shadow-lg hover:shadow-red-500/30' : ''}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.name}
                title={social.name}
              >
                <div className="w-6 h-6 md:w-7 md:h-7 lg:w-7 lg:h-7 relative z-10">
                  {social.icon === 'instagram' && (
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full transition-all duration-300">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  )}
                  {social.icon === 'youtube' && (
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full transition-all duration-300">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  )}
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Mosaic Album Section */}
        <div className="my-12 md:my-16">
          <h3 className="text-lg md:text-xl font-semibold text-gray-300 mb-4 text-center tracking-wide relative">
            Gallery
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-10 h-0.5 bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent"></div>
          </h3>
          <div className="grid grid-cols-2 gap-3 max-w-full">
            {mosaicImages.map((image, index) => (
              <a
                key={index}
                href={image.url}
                className="block relative overflow-hidden rounded-xl transition-all duration-300 aspect-square bg-gray-800/90 border border-gray-600/60 hover:-translate-y-1 hover:shadow-lg hover:border-cyan-500/60 focus:outline-none focus:ring-2 focus:ring-cyan-500/80 focus:ring-offset-2"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={image.altText}
                title={image.altText}
              >
                <img 
                  src={image.imagePath} 
                  alt={image.altText}
                  loading="lazy"
                  className="w-full h-full object-cover transition-all duration-300 hover:scale-105"
                />
              </a>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-auto pt-4">
          {/* <p>&copy; 2024 Your Name. All rights reserved.</p> */}
        </div>
      </div>

      {/* Award Popup */}
      {showPopup && selectedAward && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 animate-fadeIn" onClick={handleClosePopup}>
          <div className="bg-gray-800/90 border border-gray-600/60 rounded-2xl p-8 max-w-[90vw] max-h-[90vh] overflow-y-auto relative animate-slideIn backdrop-blur-sm" onClick={(e) => e.stopPropagation()}>
            <button className="absolute top-4 right-4 bg-transparent border-none text-3xl text-gray-300 cursor-pointer w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 hover:bg-white/10 hover:text-white" onClick={handleClosePopup}>
              ×
            </button>
            <h2 className="text-2xl font-bold text-white mb-4 pr-12">{selectedAward.title}</h2>
            <p className="text-base text-gray-300 mb-6 leading-relaxed">{selectedAward.description}</p>
            <div className="w-full flex justify-center">
              {isYouTubeVideo(selectedAward.mediaLink) ? (
                <iframe
                  src={getYouTubeEmbedUrl(selectedAward.mediaLink)}
                  title={selectedAward.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full max-w-2xl h-96 rounded-lg"
                />
              ) : (
                <img 
                  src={selectedAward.mediaLink} 
                  alt={selectedAward.title}
                  className="w-full max-w-2xl h-auto rounded-lg object-cover"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home; 